import { useSectionAccess } from "../../auth/useSectionAccess";
import { useCallback, useEffect, useState } from "react";
import { formatDateTime } from "../../lib/format";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tmApi, type TmMeetingView } from "../../api/tm";
import { useToast } from "../../components/Toast";
import type { ApiError } from "../../api/client";

export default function TmMeetingDetailPage() {
  const { canWrite } = useSectionAccess("tm_meetings");
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const toast = useToast();

  const [meeting, setMeeting] = useState<TmMeetingView | null>(null);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState("");
  const [agenda, setAgenda] = useState("");

  const refresh = useCallback(async () => {
    if (!id) return;
    const m = await tmApi.getMeeting(id);
    setMeeting(m);
    setNotes(m.notes ?? "");
    setAgenda(m.agenda ?? "");
  }, [id]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function save() {
    if (!canWrite || !id) return;
    try {
      await tmApi.updateMeeting(id, { notes, agenda });
      setEditing(false);
      await refresh();
      toast.success(t("tm.meetings.detail.saved", "Meeting saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  if (!meeting) {
    return <div style={{ padding: 24, color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>;
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Link to="/tm/meetings" style={{ color: "var(--ink-3)", fontSize: 13, textDecoration: "none" }}>
          ← {t("tm.meetings.backToList", "Back to meetings")}
        </Link>
      </div>
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>{meeting.title}</h1>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, color: "var(--ink-3)", fontSize: 13 }}>
            <div><b>{t("tm.meetings.detail.when", "When")}:</b> {formatDateTime(meeting.scheduledAt)}</div>
            <div><b>{t("tm.meetings.detail.duration", "Duration")}:</b> {t("tm.meetings.minutes", { defaultValue: "{{count}} min", count: meeting.durationMinutes })}</div>
            <div><b>{t("tm.meetings.detail.project", "Project")}:</b>{" "}
              {meeting.projectId ? <Link to={`/tm/projects/${meeting.projectId}`}>{meeting.projectName}</Link> : t("tm.meetings.detail.none", "—")}
            </div>
            <div><b>{t("tm.meetings.detail.location", "Location")}:</b> {meeting.location ?? t("tm.meetings.detail.none", "—")}</div>
            <div><b>{t("tm.meetings.detail.scribe", "Scribe")}:</b> {meeting.scribeName ?? t("tm.meetings.detail.none", "—")}</div>
            <div><b>{t("tm.meetings.detail.organizer", "Organizer")}:</b> {meeting.createdByName}</div>
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.meetings.detail.agenda", "Agenda")}</div>
            {editing && canWrite ? (
              <textarea className="input" rows={4} value={agenda} onChange={(e) => setAgenda(e.target.value)} />
            ) : (
              <div style={{ whiteSpace: "pre-wrap", color: "var(--ink-2)", fontSize: 14 }}>
                {meeting.agenda || <span style={{ color: "var(--ink-3)" }}>{t("tm.meetings.detail.none", "—")}</span>}
              </div>
            )}
          </div>

          <div>
            <div style={sectionLabel}>{t("tm.meetings.detail.notes", "Notes")}</div>
            {editing && canWrite ? (
              <textarea className="input" rows={8} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder={t("tm.meetings.detail.notesPlaceholder", "Capture decisions and action items here…")} />
            ) : (
              <div style={{ whiteSpace: "pre-wrap", color: "var(--ink-2)", fontSize: 14 }}>
                {meeting.notes || <span style={{ color: "var(--ink-3)" }}>{t("tm.meetings.detail.none", "—")}</span>}
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            {editing && canWrite ? (
              <>
                <button type="button" className="btn" onClick={() => { setEditing(false); setNotes(meeting.notes ?? ""); setAgenda(meeting.agenda ?? ""); }}>
                  {t("action.cancel", "Cancel")}
                </button>
                <button type="button" className="btn primary" onClick={save}>
                  {t("action.save", "Save")}
                </button>
              </>
            ) : (
              <button type="button" className="btn" disabled={!canWrite} onClick={() => setEditing(true)}>
                {t("action.edit", "Edit")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--ink-3)",
  letterSpacing: ".04em",
  textTransform: "uppercase",
  marginBottom: 8,
};
